import { useCallback } from "react";
import { Button, useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useReturnAfterSignIn } from "hooks/use-return-after-sign-in";
import { useDispatch } from "react-redux";
import {
  setIsUserLoggedIn,
  setAuthType,
  setUserId,
  setUserNickname,
  setUserAvatar,
  AuthType
} from "redux/slices/userDataSlice";
import { telegramOidcLogin, TelegramAuthResponse } from "api/TelegramAuth";
import {
  NONCE_MAX_AGE_MS,
  NONCE_REFRESH_MS,
  POPUP_FOCUS_GRACE_MS,
  POPUP_WATCHDOG_MS,
  telegramLoginClientId,
  useTelegramOidcPopup,
} from "hooks/useTelegramOidcPopup";
import { isUserApiKey, storeUserApiKey } from "utils/auth/apiKeyStore";

/** What the post-auth step needs to know about the Telegram user. */
interface TelegramProfile {
  id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  photo_url?: string;
}

/** The site nickname for a Telegram user: the username, else "first last" trimmed, else the first name, else "". */
export const telegramNickname = (profile: TelegramProfile): string =>
  profile.username ||
  `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
  profile.first_name ||
  "";

/**
 * The post-auth part of Telegram Login (OIDC): store the account key, update Redux + localStorage, go back. Throws when
 * the API returned no account key or no account id.
 */
const useCompleteTelegramSignIn = () => {
  const returnAfterSignIn = useReturnAfterSignIn();
  const dispatch = useDispatch();

  return useCallback(
    (response: TelegramAuthResponse, profile: TelegramProfile) => {
      // A sign-in without the account's own key is a failed sign-in. Never
      // fall back to the site's anonymous key: stored as the user key it
      // reads as "logged in" while every account call is anonymous.
      if (!isUserApiKey(response.api_key)) {
        throw new Error('Sign-in returned no API key');
      }
      // Same for the account id. Never substitute the Telegram user id: it is
      // a different id space, and the site would address someone else's row.
      if (!response.user_id) {
        throw new Error('Sign-in returned no account id');
      }

      const nickname = telegramNickname(profile);

      // Save data to localStorage
      localStorage.setItem('auth_type', AuthType.telegram);
      localStorage.setItem('user_id', String(response.user_id));
      localStorage.setItem('user_nickname', nickname);

      storeUserApiKey(dispatch, response.api_key);

      // Update Redux store
      dispatch(setIsUserLoggedIn(true));
      dispatch(setUserId(response.user_id));
      dispatch(setAuthType(AuthType.telegram));
      dispatch(setUserNickname(nickname));

      // Use Telegram data from API response if available
      const telegramData = response.telegram_data || {
        telegram_user_id: profile.id,
        username: profile.username,
        first_name: profile.first_name,
        photo_url: profile.photo_url
      };

      // Set avatar
      const photoUrl = telegramData.photo_url || profile.photo_url;
      if (photoUrl) {
        dispatch(setUserAvatar(photoUrl));
        localStorage.setItem('user_avatar', photoUrl);
      }

      // Save Telegram user data for restoration
      localStorage.setItem('telegram_data', JSON.stringify(telegramData));

      // Back to the page that required sign-in, else home
      returnAfterSignIn();
    },
    [dispatch, returnAfterSignIn]
  );
};

export {
  NONCE_MAX_AGE_MS,
  NONCE_REFRESH_MS,
  POPUP_FOCUS_GRACE_MS,
  POPUP_WATCHDOG_MS,
  telegramLoginClientId,
};

const nonceErrorId = (error: any) =>
  error?.status === 429 ? "TelegramLoginRateLimited" : "TelegramLoginUnavailable";

const loginErrorId = (error: any) => {
  if (error?.status === 429) return "TelegramLoginRateLimited";
  if (error?.status === 503) return "TelegramLoginUnavailable";
  return "TelegramLoginFailed";
};

/**
 * Telegram Login popup (OpenID Connect) — lets the user pick which Telegram
 * account signs in. The popup itself is useTelegramOidcPopup; what a token means
 * and what a failure says belong here.
 */
const TelegramOidcButton = ({ clientId }: { clientId: number }) => {
  const intl = useIntl();
  const toast = useToast();
  const completeSignIn = useCompleteTelegramSignIn();

  const showError = (descriptionId: string) => {
    toast({
      title: intl.formatMessage({ id: "TelegramLoginFailedTitle" }),
      description: intl.formatMessage({ id: descriptionId }),
      status: "error",
      position: "top",
      duration: 4000,
      isClosable: true,
    });
  };

  const { open, busy, notReady } = useTelegramOidcPopup({
    clientId,
    onToken: async ({ idToken, user }) => {
      // A token after unmount (the user navigated away with the popup open) is
      // intentional: sign-in completes and navigates to the return path.
      const response = await telegramOidcLogin(idToken);
      const data = response.telegram_data;
      completeSignIn(response, {
        id: data?.telegram_user_id ?? user?.id,
        username: data?.username ?? user?.username,
        first_name: data?.first_name ?? user?.first_name,
        last_name: user?.last_name,
        photo_url: data?.photo_url ?? user?.photo_url,
      });
    },
    onError: ({ phase, error }) => {
      if (phase === "nonce") showError(nonceErrorId(error));
      else if (phase === "library") showError("TelegramLoginUnavailable");
      else showError(loginErrorId(error));
    },
  });

  return (
    <Button
      colorScheme="blue"
      onClick={open}
      isLoading={busy || notReady}
    >
      {intl.formatMessage({ id: "TelegramLoginButton" })}
    </Button>
  );
};

/**
 * The site's only Telegram sign-in: the Telegram Login popup. A build without
 * a usable REACT_APP_TELEGRAM_LOGIN_CLIENT_ID renders no Telegram button at
 * all (the legacy widget was retired, spec 2026-10-27-telegram-oidc-login
 * Step 12).
 */
export const TelegramAuthButton = () => {
  const clientId = telegramLoginClientId();
  return clientId ? <TelegramOidcButton clientId={clientId} /> : null;
};
